import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import settingsService from '../services/settingsService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDialog } from '../contexts/DialogContext';
import Icon from './Icon';

/* ===== 상수 정의 ===== */

const GENERAL_SETTINGS = [
  {
    id: 'app-version',
    icon: 'info',
    iconColor: 'var(--color-info)',
    iconBg: 'rgba(72, 124, 165, 0.1)',
    title: '버전',
    description: '1.0.0',
  },
  {
    id: 'privacy',
    icon: 'policy',
    iconColor: 'var(--color-text-secondary)',
    iconBg: 'rgba(120, 119, 116, 0.1)',
    title: '개인정보처리방침',
    description: '개인정보 수집 및 이용 안내',
    action: 'openPrivacy',
  },
  {
    id: 'terms',
    icon: 'description',
    iconColor: 'var(--color-text-secondary)',
    iconBg: 'rgba(120, 119, 116, 0.1)',
    title: '이용약관',
    description: '서비스 이용약관 확인',
    action: 'openTerms',
  },
  {
    id: 'copyright',
    icon: 'copyright',
    iconColor: 'var(--color-inactive)',
    iconBg: 'rgba(156, 163, 175, 0.1)',
    title: '저작권',
    description: '© 2025 MoneyMind. All rights reserved.',
  },
];

const ACCOUNT_ACTIONS = [
  {
    id: 'change-password',
    icon: 'shield',
    iconColor: 'var(--color-primary)',
    iconBg: 'rgba(99, 102, 241, 0.1)',
    title: '비밀번호 변경',
    description: '계정 비밀번호 변경',
    action: 'changePassword',
  },
  {
    id: 'delete-account',
    icon: 'person_remove',
    iconColor: 'var(--color-error)',
    iconBg: 'rgba(196, 85, 77, 0.1)',
    title: '계정 탈퇴',
    description: '모든 데이터가 영구적으로 삭제됩니다',
    action: 'deleteAccount',
  },
];

const PASSWORD_MIN_LENGTH = 8;

const GENDER_OPTIONS = [
  { value: 'male', label: '남성', icon: 'male' },
  { value: 'female', label: '여성', icon: 'female' },
  { value: null, label: '선택안함', icon: 'do_not_disturb' },
];

/* ===== 서브 컴포넌트 ===== */

/**
 * SettingsItem — 설정 리스트 아이템
 */
const SettingsItem = React.memo(({ icon, iconColor, iconBg, title, description, onClick, showChevron }) => (
  <button
    type="button"
    className={`settings-item${onClick ? ' settings-item--clickable' : ''}`}
    onClick={onClick}
    tabIndex={onClick ? 0 : -1}
    aria-label={title}
  >
    <span className="settings-item__icon-wrap" style={{ background: iconBg }}>
      <Icon name={icon} size={18} color={iconColor} />
    </span>
    <span className="settings-item__content">
      <span className="settings-item__title">{title}</span>
      <span className="settings-item__description">{description}</span>
    </span>
    {showChevron && onClick && (
      <Icon name="chevron_right" size={20} color="var(--color-inactive)" className="settings-item__chevron" />
    )}
  </button>
));

SettingsItem.displayName = 'SettingsItem';
SettingsItem.propTypes = {
  icon: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  iconBg: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  showChevron: PropTypes.bool,
};

/**
 * ProfileEditModal — 프로필 수정 모달 (닉네임 + 생년월일 + 성별 + 이메일 읽기전용)
 */
const ProfileEditModal = React.memo(({ nickname, birthdate, gender, email, onSave, onClose, isLoading }) => {
  const [nicknameValue, setNicknameValue] = useState(nickname);
  const [birthdateValue, setBirthdateValue] = useState(birthdate || '');
  const [genderValue, setGenderValue] = useState(gender || null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nicknameValue.trim()) {
      onSave({
        nickname: nicknameValue.trim(),
        birthdate: birthdateValue || null,
        gender: genderValue,
      });
    }
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="profile-edit-title">
      <div className="settings-modal" onClick={(e) => e.stopPropagation()} role="document">
        <div className="settings-modal__header">
          <h3 id="profile-edit-title" className="settings-modal__title">프로필 수정</h3>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="settings-modal__body">
          <div className="form-group">
            <label className="form-label" htmlFor="edit-nickname">닉네임</label>
            <input
              id="edit-nickname"
              type="text"
              className="form-input"
              value={nicknameValue}
              onChange={(e) => setNicknameValue(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="edit-birthdate">생년월일</label>
            <input
              id="edit-birthdate"
              type="date"
              className="form-input"
              value={birthdateValue}
              onChange={(e) => setBirthdateValue(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">성별</label>
            <div className="settings-modal__radio-group" role="radiogroup" aria-label="성별 선택">
              {GENDER_OPTIONS.map((option) => (
                <label key={option.label} className={`settings-modal__radio-label ${genderValue === option.value ? 'settings-modal__radio-label--active' : ''}`}>
                  <input
                    type="radio"
                    name="gender"
                    className="settings-modal__radio-input"
                    checked={genderValue === option.value}
                    onChange={() => setGenderValue(option.value)}
                  />
                  <Icon name={option.icon} size={20} className="settings-modal__radio-icon" />
                  <span className="settings-modal__radio-text">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="edit-email">이메일</label>
            <input
              id="edit-email"
              type="email"
              className="form-input form-input--disabled"
              value={email}
              disabled
              aria-readonly="true"
            />
          </div>
          <div className="settings-modal__actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              취소
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading || !nicknameValue.trim()}>
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

ProfileEditModal.displayName = 'ProfileEditModal';
ProfileEditModal.propTypes = {
  nickname: PropTypes.string.isRequired,
  birthdate: PropTypes.string,
  gender: PropTypes.string,
  email: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

/**
 * PasswordChangeModal — 비밀번호 변경 모달
 */
const PasswordChangeModal = React.memo(({ onSave, onClose, isLoading }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`새 비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상이어야 합니다.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    onSave({ currentPassword, newPassword });
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="password-change-title">
      <div className="settings-modal" onClick={(e) => e.stopPropagation()} role="document">
        <div className="settings-modal__header">
          <h3 id="password-change-title" className="settings-modal__title">비밀번호 변경</h3>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="settings-modal__body">
          {error && (
            <div className="settings-modal__error" role="alert">
              {error}
            </div>
          )}
          <div className="form-group">
            <label className="form-label" htmlFor="current-password">현재 비밀번호</label>
            <input
              id="current-password"
              type="password"
              className="form-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="new-password">새 비밀번호</label>
            <input
              id="new-password"
              type="password"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="confirm-password">새 비밀번호 확인</label>
            <input
              id="confirm-password"
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
            />
          </div>
          <div className="settings-modal__actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              취소
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
            >
              {isLoading ? '변경 중...' : '변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

PasswordChangeModal.displayName = 'PasswordChangeModal';
PasswordChangeModal.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

/* ===== 메인 컴포넌트 ===== */

/**
 * Settings — 설정 페이지 (카드 기반 구조)
 *
 * 구조:
 * 1. 프로필 카드 (아바타 + 닉네임 + 이메일 + 뱃지 + 수정 버튼) — full width
 * 2. 2열 그리드: 앱 정보 + 계정 관리
 */
const Settings = () => {
  const { user, updateUser, handleLogout } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useDialog();

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ESC 키로 모달 닫기
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showPasswordModal) setShowPasswordModal(false);
        else if (showProfileModal) setShowProfileModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showProfileModal, showPasswordModal]);

  /** 프로필 수정 저장 */
  const handleProfileSave = useCallback(async ({ nickname, birthdate, gender }) => {
    setIsLoading(true);
    try {
      const response = await settingsService.updateProfile({ nickname, birthdate, gender });
      updateUser(response.user);
      setShowProfileModal(false);
      showToast({ type: 'success', message: '프로필이 업데이트되었습니다.' });
    } catch (error) {
      const userMessage = error?.userMessage || '프로필 업데이트에 실패했습니다.';
      showToast({ type: 'error', message: userMessage });
    } finally {
      setIsLoading(false);
    }
  }, [updateUser, showToast]);

  /** 비밀번호 변경 저장 */
  const handlePasswordSave = useCallback(async ({ currentPassword, newPassword }) => {
    setIsLoading(true);
    try {
      await settingsService.updateProfile({ currentPassword, newPassword });
      setShowPasswordModal(false);
      showToast({ type: 'success', message: '비밀번호가 변경되었습니다.' });
    } catch (error) {
      const userMessage = error?.userMessage || '비밀번호 변경에 실패했습니다.';
      showToast({ type: 'error', message: userMessage });
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  /** 로그아웃 처리 */
  const handleLogoutAction = useCallback(async () => {
    const confirmed = await confirm({
      title: '로그아웃',
      message: '정말 로그아웃하시겠습니까?',
      type: 'warning',
      confirmText: '로그아웃',
    });
    if (confirmed) {
      handleLogout();
    }
  }, [confirm, handleLogout]);

  /** 계정 탈퇴 처리 */
  const handleAccountDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: '계정 탈퇴',
      message: '정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
      type: 'error',
      confirmText: '계정 삭제',
    });
    if (!confirmed) return;

    const password = await confirm({
      title: '계정 삭제 확인',
      message: '계정 삭제를 위해 비밀번호를 입력해주세요.',
      type: 'error',
      confirmText: '계정 삭제',
      passwordRequired: true,
    });
    if (!password) return;

    try {
      await settingsService.deleteAccount(password);
      showToast({ type: 'success', message: '계정이 삭제되었습니다.' });
      localStorage.removeItem('token');
      window.location.reload();
    } catch (error) {
      const userMessage = error?.userMessage || '계정 삭제에 실패했습니다.';
      showToast({ type: 'error', message: userMessage });
    }
  }, [confirm, showToast]);

  /** 계정 관리 액션 핸들러 */
  const handleAccountAction = useCallback((action) => {
    switch (action) {
      case 'changePassword':
        setShowPasswordModal(true);
        break;
      case 'logout':
        handleLogoutAction();
        break;
      case 'deleteAccount':
        handleAccountDelete();
        break;
      default:
        break;
    }
  }, [handleLogoutAction, handleAccountDelete]);

  /** 앱 정보 액션 핸들러 (개인정보처리방침, 이용약관) */
  const handleGeneralAction = useCallback((action) => {
    switch (action) {
      case 'openPrivacy':
        window.open('https://moneymind.morucorp.com/privacy-policy', '_blank', 'noopener,noreferrer');
        break;
      case 'openTerms':
        window.open('https://moneymind.morucorp.com/terms', '_blank', 'noopener,noreferrer');
        break;
      default:
        break;
    }
  }, []);

  /** 일반 설정 아이템 (메모이제이션) */
  const generalItems = useMemo(() => GENERAL_SETTINGS, []);
  const accountItems = useMemo(() => ACCOUNT_ACTIONS, []);

  return (
    <div className="settings-page">
      {/* 1. 프로필 카드 — full width */}
      <div className="settings-profile">
        <div className="settings-profile__header">
          <div className="settings-profile__avatar">
            {user?.profile_image_url ? (
              <img
                src={user.profile_image_url}
                alt="프로필"
                className="settings-profile__avatar-img"
              />
            ) : (
              <Icon name="account_circle" size={56} color="var(--color-primary)" />
            )}
          </div>
          <div className="settings-profile__info">
            <h2 className="settings-profile__name">
              {user?.nickname || '사용자'}
            </h2>
            <p className="settings-profile__email">
              {user?.email || 'user@example.com'}
            </p>
            <span className="settings-profile__badge">
              활성 사용자
            </span>
          </div>
        </div>
        <button
          type="button"
          className="settings-profile__edit-btn"
          onClick={() => setShowProfileModal(true)}
          aria-label="프로필 수정"
        >
          <Icon name="edit" size={16} color="var(--color-primary)" />
          프로필 수정
        </button>
      </div>

      {/* 2. 2열 그리드: 앱 정보 + 계정 관리 */}
      <div className="settings-grid">
        {/* 앱 정보 섹션 */}
        <div className="settings-section">
          <h3 className="settings-section__title">앱 정보</h3>
          <div className="settings-section__list">
            {generalItems.map((item) => (
              <SettingsItem
                key={item.id}
                icon={item.icon}
                iconColor={item.iconColor}
                iconBg={item.iconBg}
                title={item.title}
                description={item.description}
                onClick={item.action ? () => handleGeneralAction(item.action) : undefined}
                showChevron={!!item.action}
              />
            ))}
          </div>
        </div>

        {/* 계정 관리 섹션 */}
        <div className="settings-section">
          <h3 className="settings-section__title">계정 관리</h3>
          <div className="settings-section__list">
            {accountItems.map((item) => (
              <SettingsItem
                key={item.id}
                icon={item.icon}
                iconColor={item.iconColor}
                iconBg={item.iconBg}
                title={item.title}
                description={item.description}
                onClick={() => handleAccountAction(item.action)}
                showChevron
              />
            ))}
          </div>
        </div>
      </div>

      {/* 프로필 수정 모달 */}
      {showProfileModal && (
        <ProfileEditModal
          nickname={user?.nickname || ''}
          birthdate={user?.birthdate || ''}
          gender={user?.gender || null}
          email={user?.email || ''}
          onSave={handleProfileSave}
          onClose={() => setShowProfileModal(false)}
          isLoading={isLoading}
        />
      )}

      {/* 비밀번호 변경 모달 */}
      {showPasswordModal && (
        <PasswordChangeModal
          onSave={handlePasswordSave}
          onClose={() => setShowPasswordModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default Settings;
